import React, { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthSkyline } from '../components/common/AuthSkyline';
import {
  PageShell,
  ProfilePageWrapper,
  ContentArea,
  WarningWrapper,
  ParentColumnArea,
  ProfilePhotoCard,
  CardHeader,
  CardContent,
} from '../styles/profile/layout.style';

// --- 1. Import all our new, clean components ---
import { ProfileSidebar, TabConfig } from '../components/profile/ProfileSidebar';
import { PayeeDetailsSection } from '../components/profile/PayeeDetailsSection';

import { PersonVcardFill, FileEarmarkTextFill, GearFill } from 'react-bootstrap-icons';
import { ParentProfileDescription } from '../components/profile/ParentProfileDescription';
import { SettingsSection } from '../components/profile/SettingsSection';
import { useUser } from '../contextManager/user.context';
import { getParentProfileStatus } from '../utils';
import { ProfileIncompleteWarning } from '../components/register/InfoBlock';
import { useScale } from './HeightScaler';
import { VerificationBanner } from '../components/verification/VerificationBanner';

// Define the tabs for a standard profile, including icons
const PROFILE_TABS: readonly TabConfig[] = [
    {label: 'Personal Details', Icon: PersonVcardFill }, 
    {label: 'Platform Rules', Icon: FileEarmarkTextFill },
    {label: 'Settings', Icon: GearFill },
];


export default function ParentProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // URL-driven tab so /profile?tab=Settings works even when already here.
  const activeTab = searchParams.get('tab') || 'Personal Details';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(tab === 'Personal Details' ? {} : { tab }, { replace: true });
  }, [setSearchParams]);
  const {userDetails} = useUser();
  const {scale} = useScale();

  const { isComplete, missingFields } = useMemo(
    () => getParentProfileStatus(userDetails),
    [userDetails]
  );

  const showWarningArea = !isComplete || !userDetails?.email_verified;

  return (
    <>
    <Helmet><title>Profile - tute</title></Helmet>
    <PageShell css={{
      height: `calc(100dvh / ${scale})`,
      position: 'relative',
    }}>
      <AuthSkyline light />
      <ProfilePageWrapper>
        <ProfileSidebar activeTab={activeTab} 
                        onTabChange={setActiveTab}
                        tabs={PROFILE_TABS}
                         />
        <ContentArea hasWarning={showWarningArea} isParent={true}>

        {showWarningArea && (
          <WarningWrapper>
            <VerificationBanner />
            {!isComplete && <ProfileIncompleteWarning missingFields={missingFields}/>}
          </WarningWrapper>
        )}

        {activeTab === 'Personal Details' && (
          <ParentColumnArea>
            <ParentProfileDescription/>
          </ParentColumnArea>
        )}
      
        {activeTab === 'Platform Rules' && (
          <div style={{ alignSelf: 'start', maxWidth: '450px' }}>
            <ProfilePhotoCard>
              <CardHeader>Platform Rules</CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gap: '1rem', fontSize: '0.85rem', color: 'var(--colors-sand12)' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>Cancellation Policy</h4>
                    <p style={{ margin: 0, color: 'var(--colors-sand11)', lineHeight: 1.5 }}>
                      You can cancel any upcoming lesson for free before it starts — your card is only charged once a lesson is completed. Lessons that have already taken place can't be cancelled.
                    </p>
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>Payment</h4>
                    <p style={{ margin: 0, color: 'var(--colors-sand11)', lineHeight: 1.5 }}>
                      Your card is securely saved when you book, but you're not charged then. Each lesson is charged only once it's completed — when your tutor provides feedback.
                    </p>
                  </div>
                </div>
              </CardContent>
            </ProfilePhotoCard>
          </div>
        )}

        {activeTab === 'Settings' && <SettingsSection />}

        </ContentArea>
      </ProfilePageWrapper>
    </PageShell>
    </>
  );
}